import type {
  BubbleChartDataPointModel,
  BubbleChartViewModelSourceData,
  BubbleChartViewInterface,
  FieldData,
} from '@ardoq/bubble-chart';
import { APIFieldType, ArdoqId } from '@ardoq/api-types';
import { referenceFields, referencedComponentFields } from './viewModel$';
import {
  type ReferenceDataToViewNodeBaseArgs,
  type ResetReferenceBubbleChartBaseArgs,
} from './types';
import { isTruthy } from '@ardoq/common-helpers';
import { isNil, uniqBy } from 'lodash';
import { getReferenceFieldPresenceData, selectedFieldValue } from '../util';
import { enhanceAttributesWithPermissions } from 'streams/fields/getFieldsForViewpoint';
import {
  isBubbleChartFieldInfo,
  getAxisTitlesForReferenceChart,
  getAxisTitlesForReferenceChartWithDirection,
  safeViewNodeDimensions,
  sortDataPointsByCluster,
} from '@ardoq/bubble-chart';
import { PermissionContext } from '@ardoq/access-control';

type ReferenceDataToViewNodeArgs = ReferenceDataToViewNodeBaseArgs & {
  viewInterface: BubbleChartViewInterface;
};
const toReferenceDataPointModel = ({
  viewInterface,
  reference,
  selectedFieldNameX,
  selectedFieldNameY,
  selectedFieldNameRadius,
}: ReferenceDataToViewNodeArgs) => {
  const { referenceId, componentId } = reference;

  const component = viewInterface.get(componentId);
  if (
    component &&
    !viewInterface.isComponentIncludedInContextByFilter(component)
  ) {
    return null;
  }

  const [selectedXValue, selectedYValue] = [
    selectedFieldNameX,
    selectedFieldNameY,
  ].map(field => selectedFieldValue(viewInterface, reference, field));
  const selectedRadiusValue =
    selectedFieldNameRadius && typeof selectedFieldNameRadius !== 'string'
      ? selectedFieldValue(viewInterface, reference, selectedFieldNameRadius)
      : 1;

  if (isNil(selectedXValue) || isNil(selectedYValue)) {
    return null;
  }

  return {
    cid: componentId,
    uniqueId: referenceId,
    ...safeViewNodeDimensions(
      selectedXValue,
      selectedYValue,
      selectedRadiusValue
    ),
    class:
      viewInterface.getComponentCssClassNames(componentId, {
        useAsBackgroundStyle: false,
      }) ?? '',
    label: viewInterface.getComponentDisplayLabel(componentId) ?? '',
    isContext: false,
  };
};

type ResetArgs = ResetReferenceBubbleChartBaseArgs & {
  permissionContext: PermissionContext;
  /**
   * Pre-computed available fields. If provided, skips re-computation.
   * Used in viewpoint mode to avoid duplicate field computation.
   */
  availableReferenceFields?: FieldData[];
  availableReferencedComponentFields?: FieldData[];
};
const resetReferenceBubbleChart = (
  previousState: BubbleChartViewModelSourceData,
  {
    viewSettings,
    permissionContext,
    incomingReferences,
    outgoingReferences,
    availableIncomingReferenceTypeNames,
    availableOutgoingReferenceTypeNames,
    numericFields,
    viewpointModeAttributes,
    availableReferenceFields: precomputedAvailableReferenceFields,
    availableReferencedComponentFields:
      precomputedAvailableReferencedComponentFields,
    hasReferenceBubbleChartFeature = false,
  }: ResetArgs
): BubbleChartViewModelSourceData => {
  const {
    selectedReferenceTypeName,
    selectedReferenceDirection,
    selectedFieldNameX,
    selectedFieldNameY,
    selectedFieldNameRadius,
    showBubblesWithZeroValue: viewSettingsShowBubblesWithZeroValue,
  } = viewSettings;

  const viewInterface = previousState.viewInterface;

  const showBubblesWithZeroValue =
    viewSettingsShowBubblesWithZeroValue === undefined ||
    viewSettingsShowBubblesWithZeroValue;

  const references =
    selectedReferenceDirection === 'incoming'
      ? incomingReferences
      : outgoingReferences;

  /**
   * Field computation differs between modes:
   * - Viewpoint mode: fields are pre-computed from the selected reference type
   *   and passed in, ensuring available fields match rendered data.
   * - Workspace mode: fields are computed here from all references in the
   *   direction, allowing field preservation when switching between reference types.
   */
  let availableReferenceFields: FieldData[];
  let availableReferencedComponentFields: FieldData[];

  if (
    precomputedAvailableReferenceFields &&
    precomputedAvailableReferencedComponentFields
  ) {
    availableReferenceFields = precomputedAvailableReferenceFields;
    availableReferencedComponentFields =
      precomputedAvailableReferencedComponentFields;
  } else {
    const referencedComponentsWorkspaceMap = new Map<ArdoqId, ArdoqId | null>(
      references.map(({ componentId }) => [
        componentId,
        viewInterface.getComponentWorkspaceId(componentId),
      ])
    );

    [availableReferenceFields, availableReferencedComponentFields] = [
      referenceFields,
      referencedComponentFields,
    ].map(getFields =>
      uniqBy(
        references
          .flatMap(linkedComponent => {
            const fieldAttributes = getFields(viewInterface, linkedComponent);

            const linkedComponentWorkspaceId =
              referencedComponentsWorkspaceMap.get(linkedComponent.componentId);

            return fieldAttributes.map(attributes =>
              enhanceAttributesWithPermissions(
                permissionContext,
                attributes,
                linkedComponentWorkspaceId
              )
            );
          })
          .filter(({ type }) => type === APIFieldType.NUMBER)
          .filter(isTruthy),
        ({ name }) => name
      )
    );
  }

  const usedReferences = numericFields.length
    ? references.filter(
        ({ referenceTypeName }) =>
          referenceTypeName === selectedReferenceTypeName
      )
    : [];

  const isViewpointMode = viewpointModeAttributes !== undefined;

  const shouldUseNewAxisLabels =
    isViewpointMode && hasReferenceBubbleChartFeature;

  const { xAxisTitle, yAxisTitle } = shouldUseNewAxisLabels
    ? getAxisTitlesForReferenceChart(viewSettings, {
        availableBubbleComponentFields: [],
        availableReferenceFields,
        availableReferencedComponentFields,
      })
    : getAxisTitlesForReferenceChartWithDirection(viewSettings);

  const unfilteredData: BubbleChartDataPointModel[] =
    !isBubbleChartFieldInfo(selectedFieldNameX) ||
    !isBubbleChartFieldInfo(selectedFieldNameY)
      ? []
      : usedReferences
          .map(reference =>
            toReferenceDataPointModel({
              viewInterface,
              reference,
              selectedFieldNameX,
              selectedFieldNameY,
              selectedFieldNameRadius,
            })
          )
          .filter(isTruthy);

  const filteredData = unfilteredData.filter(
    bubble => showBubblesWithZeroValue || bubble.radius
  );

  const data = sortDataPointsByCluster(filteredData);

  const isFilteredByRadius = Boolean(
    !viewSettings.showBubblesWithZeroValue &&
    !filteredData.length &&
    unfilteredData.length
  );

  const emptyFields = getReferenceFieldPresenceData(
    viewInterface,
    usedReferences,
    selectedFieldNameX,
    selectedFieldNameY,
    selectedFieldNameRadius
  );

  return {
    ...previousState,
    availableIncomingReferenceTypeNames,
    availableOutgoingReferenceTypeNames,
    availableReferenceFields,
    availableReferencedComponentFields,
    numericFields,
    data,
    xAxisTitle,
    yAxisTitle,
    viewSettings,
    emptyFields,
    isFilteredByRadius,
    viewpointModeAttributes,
  };
};

export { resetReferenceBubbleChart };
